import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '../state/useSession';
import { withoutPrompt } from './returnTo';

/**
 * Guards a route, remembering where the person was headed so sign-in can return them.
 *
 * It waits on `isInitializing` from AuthContext — the one-time session bootstrap — and
 * NOT on authStore's isLoading, which AuthProvider never sets during that bootstrap.
 * Reading the wrong flag meant that on every reload the user was briefly null, so a
 * signed-in person was redirected to sign-in before their session finished restoring.
 * Caught by the end-to-end reload test, not by any unit test.
 *
 * `ask="sign-up"` is the journey's door (/new, /new/idea, /new/plan). Ads bring people who
 * have no account, so they are asked to make one rather than to sign in to one they lack, and
 * the ad's attribution (`utm_*`, `fbclid`) rides along in the sign-up page's address, where
 * the pixel reads it once the visitor accepts. The idea does not: it is their own words, and
 * the whole arrival address — idea included — waits in router state, which the sign-in at the
 * end of sign-up and confirmation honours (shell/returnTo.ts).
 */
export default function RequireAuth({
  children,
  ask = 'sign-in',
}: {
  children: ReactNode;
  ask?: 'sign-in' | 'sign-up';
}) {
  const { user, isInitializing } = useSession();
  const location = useLocation();

  if (isInitializing) return null;
  if (!user) {
    const from = location.pathname + location.search;
    const to = ask === 'sign-up' ? `/sign-up${withoutPrompt(location.search)}` : '/sign-in';
    return <Navigate to={to} replace state={{ from }} />;
  }
  return <>{children}</>;
}
